import { useBrowserStore } from "@/composables/useBrowserStore";
import type {
  Peripheral,
  PeripheralPermissionStatus,
} from "@/utils/peripherals";
import { property } from "@/utils/unknown";

interface MicrophonePermissionDescriptor {
  name: "microphone";
}

declare global {
  interface Permissions {
    query(
      permissionDesc: MicrophonePermissionDescriptor
    ): Promise<PermissionStatus>;
  }
}

export type MicrophonePermissionState =
  | PermissionState
  | "unsupported"
  | "unknown";

/**
 * Microphone device (audio input)
 */
export type MicrophoneDevice = Peripheral;

const MICROPHONE_PERMISSION: MicrophonePermissionDescriptor = {
  name: "microphone",
};
const STORAGE_KEY = "audio/microphone:id";

export type PermissionChangeCallback = (state: PermissionState) => void;

const peripheralPermissionStatus = (
  state: PermissionState
): PeripheralPermissionStatus =>
  state === "prompt" ? "not-determined" : state;

export const microphone = {
  /*
   * Some browsers still expose legacy getUserMedia APIs. This helper normalizes
   * the various vendor-prefixed implementations so we always have
   * navigator.mediaDevices.getUserMedia available before recording starts.
   */
  getMedia(): void {
    if (typeof navigator === "undefined") return;

    const legacyGetUserMedia = [
      property(navigator, "getUserMedia"),
      property(navigator, "webkitGetUserMedia"),
      property(navigator, "mozGetUserMedia"),
      property(navigator, "msGetUserMedia"),
    ].find((candidate) => typeof candidate === "function");

    if (!navigator.mediaDevices) {
      Reflect.set(navigator, "mediaDevices", {});
    }

    if (
      !navigator.mediaDevices.getUserMedia &&
      typeof legacyGetUserMedia === "function"
    ) {
      navigator.mediaDevices.getUserMedia = (constraints) =>
        new Promise((resolve, reject) =>
          legacyGetUserMedia.call(navigator, constraints ?? {}, resolve, reject)
        );
    }
  },

  // Check whether the browser has access to the WebRTC API
  hasAPIAccess: (): boolean => {
    return (
      typeof navigator !== "undefined" &&
      typeof navigator.permissions?.query === "function"
    );
  },

  getPermission: async (): Promise<PeripheralPermissionStatus> => {
    if (typeof navigator === "undefined" || !navigator.permissions?.query) {
      return "unsupported";
    }

    try {
      const status = await navigator.permissions.query(MICROPHONE_PERMISSION);
      return peripheralPermissionStatus(status.state);
    } catch {
      return "unknown";
    }
  },

  queryPermission: async (): Promise<PermissionState | "unsupported"> => {
    if (!microphone.hasAPIAccess()) {
      return "unsupported";
    }

    try {
      const status = await navigator.permissions.query(MICROPHONE_PERMISSION);
      return status.state;
    } catch {
      return "unsupported";
    }
  },

  watchForPermissionChange: async (
    onChange: PermissionChangeCallback
  ): Promise<PermissionState | "unsupported"> => {
    if (!microphone.hasAPIAccess()) {
      return "unsupported";
    }

    try {
      const status = await navigator.permissions.query(MICROPHONE_PERMISSION);
      status.onchange = () => {
        onChange(status.state);
      };
      return status.state;
    } catch {
      return "unsupported";
    }
  },

  /**
   * Request microphone permission by calling getUserMedia
   *
   * This will trigger the browser's permission prompt if permission hasn't been granted.
   * After calling this, peripheral labels will be available in enumerateDevices().
   */
  requestPermission: async (): Promise<MediaStream> => {
    if (
      typeof navigator === "undefined" ||
      !navigator.mediaDevices?.getUserMedia
    ) {
      throw new Error("getUserMedia is not available");
    }
    return await navigator.mediaDevices.getUserMedia({ audio: true });
  },

  /*
   * Enumerate all available microphone (audio input) peripherals
   *
   * Note: Peripheral labels may be empty if microphone permission hasn't been granted.
   * Call requestPermission() first to request permission and get proper peripheral labels.
   */
  enumerateDevices: async (): Promise<MicrophoneDevice[]> => {
    if (
      typeof navigator === "undefined" ||
      !navigator.mediaDevices?.enumerateDevices
    ) {
      return [];
    }

    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      return devices
        .filter((d) => d.kind === "audioinput")
        .map((d) => ({
          deviceId: d.deviceId,
          label: d.label || `Microphone ${d.deviceId.slice(0, 8)}`,
          kind: d.kind,
          groupId: d.groupId,
        }));
    } catch (error) {
      window.console.warn("Failed to enumerate microphone peripherals:", error);
      return [];
    }
  },

  /*
   * Get the stored selected microphone peripheral ID from localStorage,
   * account-namespaced (adopting the value written un-namespaced by older
   * versions on first read). Raw format, like the legacy value.
   */
  getSelectedDeviceId: (accountId: string): string | null => {
    try {
      const store = useBrowserStore(accountId);
      store.migrate(STORAGE_KEY);
      return store.getRaw(STORAGE_KEY, "") || null;
    } catch {
      return null;
    }
  },

  /*
   * Save the selected microphone peripheral ID to localStorage,
   * account-namespaced
   */
  setSelectedDeviceId: (
    accountId: string,
    peripheralId: string | null
  ): void => {
    try {
      const store = useBrowserStore(accountId);
      if (peripheralId) {
        store.setRaw(STORAGE_KEY, peripheralId);
      } else {
        store.unset(STORAGE_KEY);
      }
    } catch (error) {
      window.console.warn(
        "Failed to save microphone peripheral preference:",
        error
      );
    }
  },
};
